/**
 * The model editor's form model (models.md §12): the switch, the run rule — trigger rows ("Runs when") and
 * the condition groups of the shared rule editor ("Only if") — and the options. Pure functions: record →
 * draft, draft → `ModelPatch` (only what changed), and the engine's `invalid_input` issues → the place in
 * the form they belong to.
 */

import type { FolderId, GroupNode, Model, ModelOptions, ModelPatch, ModelRun, ModelTrigger, Rule, ScoringRule, ValidationIssue } from '@digester/core';
import { SCORING_LADDER_SEED, type ModelCatalog } from '@digester/core';
import { conditionOf, groupsOf, modelsOfCondition, type EditorGroup, type Join } from '../rules/model.js';

/** One "Runs when" row: who moved the message into which folder, or "I ask". */
export interface TriggerDraft {
  on: 'automatic' | 'manual' | 'any' | 'request';
  /** A folder id or 'any' (ignored for 'request'). */
  folderId: string;
}

export interface ModelDraft {
  enabled: boolean;
  triggers: TriggerDraft[];
  join: Join;
  groups: EditorGroup[];
  flattened: boolean;
  options: ModelOptions;
}

export const TRIGGER_LABELS: Readonly<Record<TriggerDraft['on'], string>> = {
  automatic: 'A message arrives or a rule moves it into',
  manual: 'I or my assistant move a message into',
  any: 'Anyone moves a message into',
  request: 'I ask (Score now, Rescore)',
};

export function triggerDraft(t: ModelTrigger): TriggerDraft {
  return t.on === 'request' ? { on: 'request', folderId: 'any' } : { on: t.by, folderId: t.folderId };
}

export function triggerOf(t: TriggerDraft): ModelTrigger {
  return t.on === 'request' ? { on: 'request' } : { on: 'entry', folderId: t.folderId as FolderId | 'any', by: t.on };
}

export function toModelDraft(model: Model): ModelDraft {
  const { join, groups, flattened } = groupsOf(model.run.condition);
  return { enabled: model.enabled, triggers: model.run.triggers.map(triggerDraft), join, groups, flattened, options: { ...model.options, topicFields: [...model.options.topicFields] } };
}

export function runOf(draft: Pick<ModelDraft, 'triggers' | 'join' | 'groups'>): ModelRun {
  return { triggers: draft.triggers.map(triggerOf), condition: conditionOf(draft) };
}

/** JSON with object keys sorted, so two equal trees compare equal whatever order their keys were written in. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) : v,
  );
}

const same = (a: unknown, b: unknown): boolean => canonical(a) === canonical(b);

/** The patch for `models.update`: only the parts that differ from the saved record. */
export function patchOf(model: Model, draft: ModelDraft): ModelPatch {
  const patch: ModelPatch = {};
  if (draft.enabled !== model.enabled) patch.enabled = draft.enabled;
  const run = runOf(draft);
  if (!same(run, model.run)) patch.run = run;
  const options: Partial<ModelOptions> = {};
  for (const key of Object.keys(draft.options) as Array<keyof ModelOptions>) {
    if (!same(draft.options[key], model.options[key])) (options as Record<string, unknown>)[key] = draft.options[key];
  }
  if (Object.keys(options).length) patch.options = options;
  return patch;
}

/** Where an issue of the engine belongs in the editor. */
export type IssueTarget =
  | { kind: 'trigger'; index: number }
  | { kind: 'triggers' }
  | { kind: 'condition'; key: string }
  | { kind: 'option'; key: string }
  | { kind: 'general' };

/**
 * Map an issue path ('run.triggers.2', 'run.condition.children.0.children.1.values', 'options.dailyBudget')
 * onto the form: one condition group is stored flat (its conditions are the root's children), several as
 * nested groups (`conditionOf`).
 */
export function issueTarget(path: string, groupCount: number): IssueTarget {
  const parts = path.split('.');
  if (parts[0] === 'run' && parts[1] === 'triggers') return parts[2] !== undefined && /^\d+$/.test(parts[2]) ? { kind: 'trigger', index: Number(parts[2]) } : { kind: 'triggers' };
  if (parts[0] === 'run' && parts[1] === 'condition' && parts[2] === 'children' && parts[3] !== undefined && /^\d+$/.test(parts[3])) {
    const first = Number(parts[3]);
    if (groupCount <= 1) return { kind: 'condition', key: `0:${first}` };
    if (parts[4] === 'children' && parts[5] !== undefined && /^\d+$/.test(parts[5])) return { kind: 'condition', key: `${first}:${Number(parts[5])}` };
    return { kind: 'condition', key: `${first}:0` };
  }
  if (parts[0] === 'options' && parts[1]) return { kind: 'option', key: parts[1] };
  return { kind: 'general' };
}

export interface SortedIssues {
  triggers: Map<number, string>;
  triggersAll: string[];
  conditions: Map<string, string>;
  options: Map<string, string>;
  general: string[];
}

export function sortIssues(issues: readonly ValidationIssue[], groupCount: number): SortedIssues {
  const out: SortedIssues = { triggers: new Map(), triggersAll: [], conditions: new Map(), options: new Map(), general: [] };
  const add = <K>(map: Map<K, string>, key: K, message: string): void => {
    map.set(key, map.has(key) ? `${map.get(key)} ${message}` : message);
  };
  for (const issue of issues) {
    const t = issueTarget(issue.path, groupCount);
    if (t.kind === 'trigger') add(out.triggers, t.index, issue.message);
    else if (t.kind === 'triggers') out.triggersAll.push(issue.message);
    else if (t.kind === 'condition') add(out.conditions, t.key, issue.message);
    else if (t.kind === 'option') add(out.options, t.key, issue.message);
    else out.general.push(issue.path ? `${issue.path}: ${issue.message}` : issue.message);
  }
  return out;
}

/** Live scoring rules whose score conditions name `modelId`, in evaluation order (the model's "Actions on scoring"). */
export function actionsOfModel(rules: readonly Rule[], modelId: string): ScoringRule[] {
  return rules.filter((r): r is ScoringRule => r.kind === 'scoring' && r.archivedAt === null && modelsOfCondition(r.condition).includes(modelId));
}

/**
 * Is the set-up the recommended one (models.md §12 "A card on top when the set-up is not the recommended
 * one")? Every model has its catalogue defaults (switch, order, run rule, options) and the six seeded ladder
 * rows are live, enabled, unchanged and in their seeded relative order. Other scoring rules do not matter.
 */
export function isRecommendedSetUp(models: readonly Model[], catalog: ModelCatalog | undefined, rules: readonly Rule[]): boolean {
  if (!catalog || models.length === 0) return true;
  const byDefaultOrder = [...catalog.entries].sort((a, b) => a.defaults.position - b.defaults.position).map((e) => e.id as string);
  if (!same(models.map((m) => m.id as string), byDefaultOrder)) return false;
  for (const m of models) {
    const entry = catalog.entries.find((e) => e.id === m.id);
    if (!entry) continue;
    if (m.enabled !== entry.defaults.enabled || !same(m.run, entry.defaults.run) || !sameOptions(m.options, entry.defaults.options)) return false;
  }
  const live = rules.filter((r) => r.kind === 'scoring' && r.archivedAt === null);
  let last = -1;
  for (const seed of SCORING_LADDER_SEED) {
    const index = live.findIndex((r) => r.id === seed.id);
    const rule = live[index];
    if (!rule || !rule.enabled || index < last || !same(rule.condition, seed.condition) || !same(rule.actions, seed.actions)) return false;
    last = index;
  }
  return true;
}

/** The options that belong to the set-up; the daily budget and the body text length are the person's own (M6). */
function sameOptions(a: ModelOptions, b: ModelOptions): boolean {
  return a.topicMinScore === b.topicMinScore && same([...a.topicFields].sort(), [...b.topicFields].sort());
}

/** An empty "always" condition. */
export const ALWAYS: GroupNode = { type: 'group', operator: 'all', children: [] };
